import { useMemo, type ReactNode } from 'react';
import {
  PluginComponentProvider,
  type PluginComponentRegistryValue,
} from '@reflex/ui/components/registry/PluginComponentRegistry';
import { AgentStreamView } from '@/components/agent/AgentStreamView';
import { AgentConfigFormSection } from '@/components/agent/AgentConfigFormSection';
import { AgentSetupProgress } from '@/components/bubbles/AgentSetupProgress';
import { ResourcePreview } from '@/components/resource-preview/ResourcePreview';
import { EntityIdBadgeHost } from '@/components/content/EntityIdBadgeHost';
import { usePlugins } from '@/hooks/config/usePlugins';
import { getBuiltInEntityIdPreview } from '@/lib/resource-previews';

interface PluginComponentBridgeProps {
  children: ReactNode;
}

/**
 * Provides the real implementations of host-bound UI components to the
 * @reflex/ui package's component registry.
 *
 * Components like AgentStreamView, AgentConfigFormSection, and AgentSetupProgress
 * depend on web-specific infrastructure (hooks, contexts, services) that cannot
 * be moved to the ui package. Instead, the ui package exports "registry-backed"
 * components that throw if not registered. This bridge provides the real
 * implementations.
 *
 * Plugins importing these components from @reflex/ui will get the real
 * implementations when rendered inside the web host (which is always the case
 * in production). Storybook and isolated tests can provide their own mock
 * implementations via the same registry pattern.
 *
 * The type assertions are safe because the registry types are intentionally
 * looser than the real implementation types to allow for flexibility in how
 * plugins pass props. At runtime, the real implementations accept all valid
 * uses from plugin code.
 */
export function PluginComponentBridge({ children }: PluginComponentBridgeProps) {
  const { entityIdPreviews } = usePlugins();
  const components = useMemo<PluginComponentRegistryValue>(
    () => ({
      AgentStreamView: AgentStreamView as PluginComponentRegistryValue['AgentStreamView'],
      AgentConfigFormSection:
        AgentConfigFormSection as PluginComponentRegistryValue['AgentConfigFormSection'],
      AgentSetupProgress: AgentSetupProgress as PluginComponentRegistryValue['AgentSetupProgress'],
      ResourcePreview: ResourcePreview as PluginComponentRegistryValue['ResourcePreview'],
      EntityIdBadge: EntityIdBadgeHost as PluginComponentRegistryValue['EntityIdBadge'],
      // The same prefix -> preview resolution `EntityIdBadgeHost` performs,
      // exposed as a label lookup so plugin surfaces can name what an id is.
      entityIdKindLabel: (prefix: string) =>
        (entityIdPreviews.get(prefix) ?? getBuiltInEntityIdPreview(prefix))?.label,
    }),
    [entityIdPreviews],
  );

  return <PluginComponentProvider components={components}>{children}</PluginComponentProvider>;
}
